import { IApiLeveragesArray } from '@api/interfaces/data-contracts';
import { IApiTradingAccountUserSettings } from '@api/services/tradingAccountUserSettings.apiService';
import { EnumUTAFormTAField } from '@components-member/TradingAccounts/AccountCreate/enums';
import { IModalFormField } from '@components-member/socialTrading/social.interfaces';
import { ibCodeFieldIsEnabled, setTooltipMessage } from '@components-member/socialTrading/utils';
import CONSTANTS from '@components-shared/constants';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { keyMap } from '@locales/lokalise/keyMap';
import { TRADING_ACCOUNT_FORM_TYPE, TRADING_ACCOUNT_SOURCE } from 'global.enums';
import { ClientSourceType } from 'global.interfaces';
import i18next from 'i18next';

export const getTitle = (source: TRADING_ACCOUNT_SOURCE, tradingAccountType: TRADING_ACCOUNT_FORM_TYPE): string => {
  if (source === TRADING_ACCOUNT_SOURCE.DEFAULT) {
    return i18next.t(keyMap.tradingAccountsOpenAccountModalTitle);
  }

  switch (tradingAccountType) {
    case TRADING_ACCOUNT_FORM_TYPE.MASTER:
      return i18next.t(keyMap.menuOpenMastersAccount);
    case TRADING_ACCOUNT_FORM_TYPE.INVESTOR:
      return i18next.t(keyMap.menuOpenInvestorsAccount);
    default:
      return i18next.t(keyMap.tradingAccountsOpenAccountModalTitle);
  }
};

type SetIbCodeTooltipProps = {
  fields: IModalFormField[];
  canSetIb: boolean;
  clientSourceType: ClientSourceType;
  ibCodePrefix: string;
};

type SetIbCodeTooltip = (args: SetIbCodeTooltipProps) => void;
export const setIbCodeTooltip: SetIbCodeTooltip = (args) => {
  const { ibCodePrefix, fields, clientSourceType, canSetIb } = args;

  const ibCodeIsEnabled = ibCodeFieldIsEnabled(canSetIb, clientSourceType);

  if (!ibCodeIsEnabled) {
    return;
  }

  setTooltipMessage(
    FormUtils.getFieldIndex(fields, EnumUTAFormTAField.ibCode),
    i18next.t(keyMap.formsInputTooltipsIbCode, { ibCodePrefix }),
    fields,
  );
};

export const getAccountType = <T extends { id: number }>(accounts: T[], value: number): T | undefined => {
  return accounts.find((acc) => acc.id === value);
};

export const changeLeverage = <T extends { leverages: number[] }>(
  leverageIdx: number,
  accountType: T,
  fields: IModalFormField[],
): void => {
  const options = getLeverageOptions(accountType.leverages);

  FormUtils.updateFormField(leverageIdx, { options }, fields);

  FormUtils.updateFormField(leverageIdx, { isHidden: false }, fields);
};

export const getLeverageOptions = (leverages?: IApiLeveragesArray): { value: number }[] => {
  if (!leverages) {
    return [];
  }
  return leverages.map((option) => ({ value: option }));
};

export const accountLimitForUnverifiedUsersExceeded = (
  userProfileStatus: string,
  selectedAccountType: TRADING_ACCOUNT_FORM_TYPE,
  creationSettings: IApiTradingAccountUserSettings,
): boolean => {
  const isApproved = userProfileStatus === CONSTANTS.PROFILE_STATUS.APPROVED;

  if (isApproved) {
    return false;
  }

  const { INVESTOR, MASTER, SELF, DEMO } = CONSTANTS.TRADING_ACCOUNT_FORM_TYPE;

  const { canCreateRealAccountForNonApproved, canCreateDemoAccountForNonApproved } = creationSettings;

  if (selectedAccountType === SELF || selectedAccountType === MASTER || selectedAccountType === INVESTOR) {
    return !canCreateRealAccountForNonApproved;
  }

  if (selectedAccountType === DEMO) {
    return !canCreateDemoAccountForNonApproved;
  }

  return false;
};
